import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { FileBox, X, Loader2, AlertCircle } from 'lucide-react';
import { useSpineImportStore, useSpineRuntimeStore } from '../../../store/spineStore';
import { useProjectPanelStore } from '../../../store/projectPanelStore';
import { registerSpineFromAssets } from '../../../utils/spine-discovery';
import { Asset } from '../../../../core/types/asset.types';
import { cn } from '../../../utils/cn';

interface SpineAssetPickerProps {
  value: string | null;
  /** Called with (assetId, skeletonPath). The component data stores spineAssetPath too. */
  spineAssetPath?: string | null;
  onChange: (assetId: string | null, assetPath?: string | null) => void;
  disabled?: boolean;
}

/** A single drop field that accepts a specific spine file type */
function SpineDropField({
  label,
  icon,
  accept,
  asset,
  displayName,
  onAssign,
  onClear,
  disabled,
}: {
  label: string;
  icon: string;
  accept: string[];
  asset: Asset | null;
  displayName?: string | null;
  onAssign: (asset: Asset) => void;
  onClear: () => void;
  disabled?: boolean;
}) {
  const [dragOver, setDragOver] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (disabled) return;

    const assetId = e.dataTransfer.getData('text/plain');
    if (!assetId) return;

    const assets = useProjectPanelStore.getState().assets;
    const dropped = assets[assetId];
    if (!dropped) return;

    const ext = dropped.extension.toLowerCase();
    if (!accept.includes(ext)) return;

    onAssign(dropped);
  };

  // Show assigned state if we have an Asset object or a restored display name
  const name = asset?.name || displayName;
  if (name) {
    return (
      <div
        className={cn(
          'flex items-center gap-1.5 bg-zinc-800 border rounded p-1 transition-colors',
          dragOver
            ? 'border-purple-500 bg-purple-500/10'
            : 'border-zinc-700 hover:border-zinc-500',
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <span className="text-sm flex-shrink-0 pl-0.5">{icon}</span>
        <span className="text-[10px] text-zinc-300 flex-1 truncate" title={name}>
          {name}
        </span>
        {!disabled && (
          <button
            className="p-0.5 rounded hover:bg-zinc-600 text-zinc-500 hover:text-red-400 flex-shrink-0"
            onClick={onClear}
            title={`Clear ${label}`}
          >
            <X size={10} />
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex items-center justify-center gap-1.5 border-2 border-dashed rounded py-1.5 transition-colors',
        dragOver
          ? 'border-purple-500 bg-purple-500/10'
          : 'border-zinc-600 hover:border-zinc-500',
        disabled && 'opacity-50 pointer-events-none'
      )}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <span className="text-xs">{icon}</span>
      <span className="text-[10px] text-zinc-500">Drag {label} here</span>
    </div>
  );
}

export const SpineAssetPicker: React.FC<SpineAssetPickerProps> = ({
  value,
  spineAssetPath,
  onChange,
  disabled = false,
}) => {
  const importedAssets = useSpineImportStore((s) => s.importedAssets);
  const loadedAssets = useSpineRuntimeStore((s) => s.loadedAssets);

  const [skelAsset, setSkelAsset] = useState<Asset | null>(null);
  const [atlasAsset, setAtlasAsset] = useState<Asset | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Resolve the effective spine asset — by ID or by skeletonPath fallback
  const resolvedSpineAsset = useMemo(() => {
    if (!value) return null;
    // Direct ID lookup
    const byId = loadedAssets[value] || importedAssets.find((a) => a.id === value);
    if (byId) return byId;
    // Fallback: match by skeleton path (after reload, ID changes)
    if (spineAssetPath) {
      const byPath = Object.values(loadedAssets).find(
        (a) => a.skeletonPath === spineAssetPath
      );
      if (byPath) return byPath;
    }
    return null;
  }, [value, spineAssetPath, loadedAssets, importedAssets]);

  // Derive display names from stored paths (for showing in drop fields after reload)
  const skelDisplayName = useMemo(() => {
    if (skelAsset) return null; // local state takes priority
    if (resolvedSpineAsset) return resolvedSpineAsset.skeletonPath.split('/').pop() || null;
    if (spineAssetPath) return spineAssetPath.split('/').pop() || null;
    return null;
  }, [skelAsset, resolvedSpineAsset, spineAssetPath]);

  const atlasDisplayName = useMemo(() => {
    if (atlasAsset) return null;
    if (resolvedSpineAsset) return resolvedSpineAsset.atlasPath.split('/').pop() || null;
    return null;
  }, [atlasAsset, resolvedSpineAsset]);

  // Combine imported and loaded assets for dropdown
  const availableAssets = useMemo(() => {
    const assetMap = new Map<string, { id: string; name: string; path: string }>();
    for (const asset of importedAssets) {
      assetMap.set(asset.id, { id: asset.id, name: asset.name, path: asset.path });
    }
    for (const asset of Object.values(loadedAssets)) {
      if (!assetMap.has(asset.id)) {
        assetMap.set(asset.id, { id: asset.id, name: asset.name, path: asset.path });
      }
    }
    return Array.from(assetMap.values());
  }, [importedAssets, loadedAssets]);

  // The effective dropdown value — use resolved asset's ID (may differ from stored value)
  const effectiveValue = resolvedSpineAsset?.id || value;

  const selectedAsset = useMemo(() => {
    if (!effectiveValue) return null;
    return availableAssets.find((a) => a.id === effectiveValue) || null;
  }, [effectiveValue, availableAssets]);

  // When both files are assigned, auto-register
  const tryRegister = useCallback(
    async (skel: Asset, atlas: Asset) => {
      setError(null);
      setIsLoading(true);
      try {
        const result = await registerSpineFromAssets(skel, atlas);
        if (result.error) {
          setError(result.error);
        } else {
          const registered =
            useSpineRuntimeStore.getState().loadedAssets[result.assetId];
          onChange(result.assetId, registered?.skeletonPath || null);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load spine asset');
      } finally {
        setIsLoading(false);
      }
    },
    [onChange]
  );

  const handleSkelAssign = useCallback(
    (asset: Asset) => {
      setSkelAsset(asset);
      setError(null);
      if (atlasAsset) tryRegister(asset, atlasAsset);
    },
    [atlasAsset, tryRegister]
  );

  const handleAtlasAssign = useCallback(
    (asset: Asset) => {
      setAtlasAsset(asset);
      setError(null);
      if (skelAsset) tryRegister(skelAsset, asset);
    },
    [skelAsset, tryRegister]
  );

  const handleSkelClear = useCallback(() => {
    setSkelAsset(null);
    setAtlasAsset(null);
    setError(null);
    onChange(null, null);
  }, [onChange]);

  const handleAtlasClear = useCallback(() => {
    setAtlasAsset(null);
    setError(null);
    // Clearing atlas also clears the full spine asset since both are needed
    onChange(null, null);
  }, [onChange]);

  const handleSelect = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const id = e.target.value || null;
      const asset = id ? availableAssets.find((a) => a.id === id) : null;
      onChange(id, asset?.path || null);
    },
    [onChange, availableAssets]
  );

  const handleClear = useCallback(() => {
    onChange(null, null);
    setSkelAsset(null);
    setAtlasAsset(null);
    setError(null);
  }, [onChange]);

  // If the resolved asset has a different ID than stored, update the component data
  useEffect(() => {
    if (resolvedSpineAsset && value && resolvedSpineAsset.id !== value) {
      onChange(resolvedSpineAsset.id, resolvedSpineAsset.skeletonPath);
    }
  }, [resolvedSpineAsset, value, onChange]);

  return (
    <div className="space-y-1.5">
      {/* Two drop fields */}
      <div className="grid grid-cols-2 gap-1.5">
        <SpineDropField
          label=".skel"
          icon="💀"
          accept={['.skel', '.json']}
          asset={skelAsset}
          displayName={skelDisplayName}
          onAssign={handleSkelAssign}
          onClear={handleSkelClear}
          disabled={disabled}
        />
        <SpineDropField
          label=".atlas"
          icon="🗺️"
          accept={['.atlas']}
          asset={atlasAsset}
          displayName={atlasDisplayName}
          onAssign={handleAtlasAssign}
          onClear={handleAtlasClear}
          disabled={disabled}
        />
      </div>

      {/* Loading / error feedback */}
      {isLoading && (
        <div className="flex items-center gap-1.5 px-1">
          <Loader2 size={12} className="text-purple-400 animate-spin" />
          <span className="text-[10px] text-purple-400">
            Discovering spine files...
          </span>
        </div>
      )}
      {error && (
        <div className="flex items-center gap-1.5 px-1">
          <AlertCircle size={12} className="text-red-400 flex-shrink-0" />
          <span className="text-[10px] text-red-400">{error}</span>
        </div>
      )}

      {/* Dropdown + clear button */}
      <div className="flex items-center gap-2">
        <FileBox className="w-4 h-4 text-purple-400 flex-shrink-0" />
        <select
          value={effectiveValue || ''}
          onChange={handleSelect}
          disabled={disabled}
          className={cn(
            'flex-1 bg-zinc-800 border border-zinc-700 rounded px-2 py-1.5 text-sm',
            'text-zinc-200 focus:outline-none focus:border-blue-500',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
        >
          <option value="">None</option>
          {availableAssets.map((asset) => (
            <option key={asset.id} value={asset.id}>
              {asset.name}
            </option>
          ))}
        </select>
        {effectiveValue && (
          <button
            onClick={handleClear}
            disabled={disabled}
            className={cn(
              'p-1 text-zinc-500 hover:text-red-400 hover:bg-zinc-700 rounded',
              'transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
            )}
            title="Clear asset"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Selected asset path */}
      {selectedAsset && (
        <div
          className="text-xs text-zinc-500 pl-6 truncate"
          title={selectedAsset.path}
        >
          {selectedAsset.path || 'No path'}
        </div>
      )}

      {/* Metadata summary */}
      {resolvedSpineAsset && (
        <div className="text-xs text-zinc-600 pl-6">
          {resolvedSpineAsset.animations.length} animations,{' '}
          {resolvedSpineAsset.skins.length} skins
        </div>
      )}

      {availableAssets.length === 0 && !error && !skelAsset && !atlasAsset && !skelDisplayName && (
        <div className="text-xs text-zinc-500 pl-6">
          No Spine assets imported. Use File &gt; Import Spine...
        </div>
      )}
    </div>
  );
};
